<template>
  <div>
    <ToastificationLoadingContent ref="loadingToast" />
    <ToastificationProgressContent ref="progressToast" />

    <Sidenav
      :is-sidebar-active.sync="isSidebarActive"
      :is-add.sync="isAdd"
      :item-edit.sync="itemEdit"
      :serverType="serverType"
      @refetch-data="refreshDevices"
    />

    <b-row class="pt-2 mx-2">
      <b-col cols="6" class="d-flex align-items-center px-0">
        <BreadcrumbMenu />
      </b-col>
      <b-col cols="6" class="text-right px-0">
        <button-filters
          @show-modal="$root.$emit('bv::show::modal', $event)"
          :filters-count="filtersCount"
          @clear-search-filters="clearSearchFilters"
        />
      </b-col>
    </b-row>

    <!-- Filters -->
    <filter-modal @action="buildFilters()" @clear="clearSearchFilters()">
      <template v-slot:main-filters>
        <FilterDevices
          :serverFilter.sync="serverFilter"
          :cameraFilter.sync="cameraFilter"
          :cameraOptions="cameraOptions"
          :statusActiveFilter.sync="statusActiveFilter"
          :camerasOnlineFilter.sync="camerasOnlineFilter"
          :startDateFilter.sync="startDateFilter"
          :endDateFilter.sync="endDateFilter"
          :cameraProxyFilter.sync="cameraProxyFilter"
          :serve.sync="serverFilter"
          :camerasTypesFilter.sync="camerasTypesFilter"
          :camerasTypesOptions="camerasTypesOptions"
        />
      </template>
    </filter-modal>

    <!-- Table Container Card -->
    <b-card class="m-2" no-body>
      <div class="m-2">
        <!-- Table Top -->
        <b-row>
          <!-- Device count-->
          <b-col
            cols="12"
            md="6"
            class="d-flex align-items-center justify-content-start mb-1 mb-md-0"
          >
            <PerPageSelector :perPage.sync="perPage" :perPageOptions="perPageOptions" />
          </b-col>

          <!-- Search -->
          <b-col cols="12" md="6">
            <div class="d-flex align-items-center justify-content-end">
              <b-form-input
                v-model="searchQuery"
                class="d-inline-block mr-1"
                :placeholder="$t('table.search')"
              />
              <b-button
                variant="primary"
                @click="openAdd()"
                v-if="$can('create', 'AiAnalyticsServers')"
              >
                <span class="text-nowrap text-capitalize">{{
                  `${$t("actions.add")} ${$tc("ai_analytics_servers.device", 1)}`
                }}</span>
              </b-button>
              <b-button
                @click="exportDevices()"
                variant="success"
                class="text-capitalize ml-1"
                type="button"
                :disabled="buttonExport"
                v-b-tooltip.hover
                title="Exportar dispositivos"
              >
                <feather-icon icon="DownloadCloudIcon" size="15" />
              </b-button>
            </div>
          </b-col>
        </b-row>
      </div>

      <b-table
        ref="refListTable"
        class="position-relative"
        :items="fetchList"
        responsive
        :fields="tableColumns"
        primary-key="id"
        :sort-by.sync="sortBy"
        :sort-desc.sync="isSortDirDesc"
        show-empty
        :empty-text="$t('no_record_found')"
      >
        <template #head()="data">
          <span> {{ $tc(data.label, 1) }}</span>
        </template>

        <!-- Column: Actions -->
        <template #cell(actions)="data">
          <b-button
            :id="`actions-${data.item.id}`"
            tabindex="0"
            variant="white"
            class="px-1 btn-y"
          >
            <feather-icon
              icon="MoreVerticalIcon"
              size="16"
              class="align-middle text-body"
            />
          </b-button>
          <b-popover
            :target="`actions-${data.item.id}`"
            triggers="focus"
            placement="bottom"
            custom-class="popover-p-0"
            variant="white"
          >
            <b-list-group class="m-0">
              <b-list-group-item
                v-if="$can('update', 'AiAnalyticsServers')"
                class="border-0"
                button
                @click="openEdit(data.item)"
              >
                <feather-icon icon="EditIcon" />
                <span class="align-middle ml-50 text-capitalize">
                  {{ $t("actions.edit") }}
                </span>
              </b-list-group-item>

              <b-list-group-item
                v-if="$can('delete', 'AiAnalyticsServers')"
                class="border-0"
                button
                @click="confirmDelete(data.item)"
              >
                <feather-icon icon="TrashIcon" />
                <span class="align-middle ml-50 text-capitalize">
                  {{ $t("actions.delete") }}
                </span>
              </b-list-group-item>
            </b-list-group>
          </b-popover>
        </template>

        <!-- Column: Status -->
        <template #cell(status)="data">
          <b-badge :variant="data.item.status ? 'success' : 'danger'">
            {{ data.item.status ? "Online" : "Offline" }}
          </b-badge>
        </template>

        <template #cell(on_server_status)="data">
          <b-form-checkbox
            :checked="data.item.on_server_status"
            switch
            @change="updateStatus(data.item)"
          />
        </template>

        <template #cell(server.server_type)="data">
          <span v-if="data.item.server.server_type === 'fire_smoke'">
            {{ getDescriptionFireSmokeServer(data.item) }}
          </span>
          <span v-else class="text-muted">-</span>
        </template>

        <template #cell(camera.camera_type)="data">
          <span>{{ getDescriptionCameraType(data.item.camera.camera_type) }}</span>
        </template>

        <!-- <template #cell(fire_threshold)="data">
          <span
            v-if="
              data.item.fire_threshold !== null && data.item.fire_threshold !== undefined
            "
          >
            {{ Math.round(data.item.fire_threshold * 100) }}%
          </span>
          <span v-else class="text-muted">-</span>
        </template> -->

      <!--   <template #cell(smoke_threshold)="data">
          <span
            v-if="
              data.item.smoke_threshold !== null &&
              data.item.smoke_threshold !== undefined
            "
          >
            {{ Math.round(data.item.smoke_threshold * 100) }}%
          </span>
          <span v-else class="text-muted">-</span>
        </template> -->

        <!-- <template #cell(use_local_confirmation)="data" class="mx-auto">
          <b-badge :variant="data.item.use_local_confirmation ? 'success' : 'secondary'">
            {{ data.item.use_local_confirmation ? $t('yes') : $t('no') }}
          </b-badge>
        </template> -->
      </b-table>
      <TableFooter
        :dataMeta="dataMeta"
        :totalList="totalList"
        :currentPage="currentPage"
        :perPage="perPage"
        @updatePagination="updatePagination"
      />
    </b-card>
  </div>
</template>

<script>
import { ref, computed, onMounted } from "@vue/composition-api";
import { BFormCheckbox } from "bootstrap-vue";
import useList from "./useList";
import Sidenav from "./Sidenav.vue";
import FilterDevices from "./Filter.vue";
import { useToast } from "vue-toastification/composition";
import ToastificationContent from "@core/components/toastification/ToastificationContent.vue";
import ToastificationLoadingContent from "@core/components/toastification/ToastificationLoadingContent.vue";
import ToastificationProgressContent from "@core/components/toastification/ToastificationProgressContent.vue";
import store from "@/store";
import router from "@/router";
import { useUtils as useI18nUtils } from "@core/libs/i18n";
import BreadcrumbMenu from "@/components/BreadcrumbMenu.vue";
import TableFooter from "@/components/TableFooter.vue";
import PerPageSelector from "@/components/PerPageSelector.vue";
import FilterModal from "@/components/Filters/FilterModal.vue";
import ButtonFilters from "@/components/Filters/ButtonFilters.vue";
import axios from '@axios';

export default {
  name: "AiAnalyticsDevices",
  components: {
    Sidenav,
    FilterDevices,
    BreadcrumbMenu,
    TableFooter,
    PerPageSelector,
    ToastificationLoadingContent,
    ToastificationProgressContent,
    BFormCheckbox,
    FilterModal,
    ButtonFilters,
  },
  setup() {
    onMounted(() => getDeviceCameras());

    const loadingToast = ref(null);
    const isSidebarActive = ref(false);
    const isAdd = ref(false);
    const itemEdit = ref({});
    const buttonExport = ref(false);

    const openEdit = (item) => {
      isSidebarActive.value = true;
      itemEdit.value = JSON.parse(JSON.stringify(item));
      isAdd.value = false;
    };

    const { t } = useI18nUtils();
    const toast = useToast();

    const trans = {
      "messages.delete.title": t("messages.delete.title"),
      "messages.delete.body": t("messages.delete.body"),
      "actions.delete": t("actions.delete"),
      "actions.cancel": t("actions.cancel"),
      "ai_analytics_servers.uuid_not_found": t("ai_analytics_servers.uuid_not_found"),
      "ai_analytics_servers.restarted_successfully": t("ai_analytics_servers.restarted_successfully"),
      "ai_analytics_servers.restart_error": t("ai_analytics_servers.restart_error"),
      "ai_analytics_servers.fetch_error": t("ai_analytics_servers.fetch_error"),
      "ai_analytics_servers.delete_error": t("ai_analytics_servers.delete_error"),
      "ai_analytics_servers.update_status_error": t("ai_analytics_servers.update_status_error"),
    };

    const {
      tableColumns,
      fetchList,
      devicesCount,
      serverFilter,
      tenantFilter,
      cameraFilter,
      statusActiveFilter,
      camerasOnlineFilter,
      startDateFilter,
      endDateFilter,
      cameraProxyFilter,
      tenantOptions,
      cameraOptions,
      filtersCount,
      clearSearchFilters,
      buildFilters,
      refListTable,
      perPage,
      totalList,
      currentPage,
      searchQuery,
      sortBy,
      isSortDirDesc,
      perPageOptions,
      dataMeta,
      refetchData,
      updatePagination,
      serverType,
      getDeviceCameras,
      camerasTypesOptions,
      camerasTypesFilter,
      filters
    } = useList(trans);

    const confirmDelete = function (item) {
      const self = this;
      const storeInner = store;
      this.$swal({
        title: trans["messages.delete.title"],
        text: trans["messages.delete.body"],
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: trans["actions.delete"],
        cancelButtonText: trans["actions.cancel"],
        customClass: {
          confirmButton: "btn btn-primary text-capitalize",
          cancelButton: "btn btn-outline-danger ml-1 text-capitalize",
        },
        buttonsStyling: false,
      }).then((result) => {
        if (result.value) {
          storeInner
            .dispatch("ai_analytics_servers/devices/delete", {
              id_camera_proxy: item.id_camera_proxy,
              id_server: item.id_analytic_server,
            })
            .then((response) => {
              if (response.success) {
                toast({
                  component: ToastificationContent,
                  props: {
                    title: String(response.message),
                    icon: "CheckIcon",
                    variant: "success",
                  },
                });

                self.refetchData();
              } else {
                toast(
                  {
                    component: ToastificationContent,
                    props: {
                      title: String(response.message),
                      icon: "AlertTriangleIcon",
                      variant: "danger",
                    },
                  },
                  {
                    timeout: 5000,
                  }
                );
              }
            })
            .catch((error) => {
              const isLocalChange = error.response?.status === 400;

              toast(
                {
                  component: ToastificationContent,
                  props: {
                    title: String(
                      error.response?.data?.message || trans["ai_analytics_servers.delete_error"]
                    ),
                    icon: "AlertTriangleIcon",
                    variant: isLocalChange ? "warning" : "danger",
                  },
                },
                {
                  timeout: 5000,
                }
              );

              if (isLocalChange) {
                self.refetchData();
              }
            });
        }
      });
    };

    const updateStatus = (item) => {
      const newStatus = item.on_server_status ? 0 : 1;

      store
        .dispatch("ai_analytics_servers/devices/updateStatus", {
          id_server: item.id_analytic_server,
          id_camera_proxy: item.id_camera_proxy,
          status: newStatus,
        })
        .then((response) => {
          if (response.success) {
            toast({
              component: ToastificationContent,
              props: {
                title: String(response.message),
                icon: "CheckIcon",
                variant: "success",
              },
            });
            refetchData();
          } else {
            toast({
              component: ToastificationContent,
              props: {
                title: String(response.message),
                icon: "AlertTriangleIcon",
                variant: "danger",
              },
            });
          }
        })
        .catch((error) => {
          const isLocalChange = error.response?.status === 400;

          toast({
            component: ToastificationContent,
            props: {
              title: String(error.response?.data?.message || trans["ai_analytics_servers.update_status_error"]),
              icon: "AlertTriangleIcon",
              variant: isLocalChange ? "warning" : "danger",
            },
          });

          if (isLocalChange) {
            refetchData();
          }
        });
    };

    const openAdd = () => {
      isAdd.value = true;
      isSidebarActive.value = true;
    };

    const isDark = computed(() => store.state.appConfig.layout.skin === "dark");

    const goBack = () => {
      router.go(-1);
    };

    const getDescriptionFireSmokeServer = (item) => {
      if (!item.server) {
        return '-';
      }

      const fire  = Math.round(item.config.fire_threshold * 100);
      const smoke = Math.round(item.config.smoke_threshold * 100);
      const useLocalConfirmationText = (item.config.use_local_confirmation) ? 'SI' : 'NO';

      return `F ${fire}% - H ${smoke}% - ${useLocalConfirmationText}`;
    }

    const getDescriptionCameraType = (cameraType) => {
      let type = cameraType.split('\\').pop();

      if (type === 'VxgCamera') {
        type = 'Omnicloud';
      }

      return type;
    }

    const refreshDevices = () => {
      refetchData();
      getDescriptionFireSmokeServer();
    }

    const exportDevices = async () => {
      try {
        buttonExport.value = true;
        const responseType = "blob";
        const response = await axios.get('v1/ai-analytics/servers/devices/export', {
          params: { filters: filters.value },
          responseType
        });

        const url = window.URL.createObjectURL(
          new Blob([response.data], {
            type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
          })
        );

        const filename = response.headers["content-disposition"].split("filename=")[1];

        const link = document.createElement("a");

        link.href = url;

        link.setAttribute("download", filename);
        document.body.appendChild(link);
        link.click();
        link.remove();

        const notification = {
          id: filename,
          progress: 100,
          completed: true,
          view: "users",
          url: url,
          download: filename,
          title: "Reporte Forms. Seguridad operativa",
          body: "Descarga completa",
          error: false,
          timestamp:
            new Date().toLocaleDateString() + " " + new Date().toLocaleTimeString(),
        };

        store.dispatch("grid/updateDownloadNotification", notification);
      } catch (error) {
        const { statusText } = error.response;

        toast({
          component: ToastificationContent,
          props: {
            title: statusText,
            icon: "AlertTriangleIcon",
            variant: "danger",
          },
        });
      } finally {
        buttonExport.value = false;
      }
    }

    return {
      isSidebarActive,
      isAdd,
      itemEdit,
      buttonExport,
      openEdit,
      openAdd,
      confirmDelete,
      tableColumns,
      fetchList,
      searchQuery,
      sortBy,
      isSortDirDesc,
      refListTable,
      refetchData,
      updateStatus,
      serverFilter,
      tenantFilter,
      cameraFilter,
      statusActiveFilter,
      camerasOnlineFilter,
      startDateFilter,
      endDateFilter,
      cameraProxyFilter,
      tenantOptions,
      cameraOptions,
      filtersCount,
      clearSearchFilters,
      buildFilters,
      isDark,
      goBack,
      perPage,
      totalList,
      currentPage,
      perPageOptions,
      dataMeta,
      updatePagination,
      serverType,
      getDescriptionFireSmokeServer,
      getDescriptionCameraType,
      refreshDevices,
      camerasTypesOptions,
      camerasTypesFilter,
      exportDevices
    };
  },
};
</script>
